import GameScene from "../scenes/GameScene";
import ArrayUtils from "../utils/ArrayUtils";
import { TiledMap, Tileset } from "../utils/TiledMap";
import { HiddenObject } from "./HiddenObject";
import { HiddenObjectData } from "./HiddenObjectData";
import { PointsIndicator } from "./PointsIndicator";

export type HiddenObjectLevelConfig = {
	objects: number;
};

export class HiddenObjectLevel {
	static ON_OBJECT_COLLECTED = "ON_OBJECT_COLLECTED";
	static ON_LEVEL_COMPLETED = "ON_LEVEL_COMPLETED";

	objectsEnabled: number;
	objectsFound: number;
	totalObjects: number;
	score: number;
	events: Phaser.Events.EventEmitter;
	pointIndicators: PointsIndicator[];

	hiddenObjects: HiddenObject[];
	scene: GameScene;

	constructor(level: TiledMap, scene: GameScene, config: HiddenObjectLevelConfig) {
		this.events = new Phaser.Events.EventEmitter();
		this.hiddenObjects = [];
		this.scene = scene;

		const tileset = scene.cache.json.get("flurry-tileset") as Tileset;
		const backgrounds = [];
		const objects = [];
		let objLayer = 0;
		this.objectsFound = 0;
		this.objectsEnabled = 0;
		this.totalObjects = config.objects;
		this.score = 0;
		this.pointIndicators = [];

		for (const layer of level.layers) {
			if (layer.type == "imagelayer") {
				const split = layer.image.split("/");
				const image = split[split.length - 1];
				backgrounds.push(image);
			} else if (layer.type == "objectgroup") {
				for (const obj of layer.objects) {
					const gidData = Phaser.Tilemaps.Parsers.Tiled.ParseGID(obj.gid);

					const tile = tileset.tiles.find((t) => t.id == gidData.gid - 1);
					const split = tile.image.split("/");
					const image = split[split.length - 1];

					const flipHorizontal = gidData.flippedHorizontal || gidData.flippedAntiDiagonal;
					const flipVertical = gidData.flippedVertical || gidData.flippedAntiDiagonal;

					const objectData: HiddenObjectData = {
						image: image,
						layer: objLayer,
						x: obj.x,
						y: obj.y,
						rotation: obj.rotation,
						width: obj.width,
						height: obj.height,
						flipX: flipHorizontal,
						flipY: flipVertical,
					};
					objects.push(objectData);
				}
				objLayer++;
			}
		}

		this.scene.hiddenObjGame.createBGLayers(backgrounds);
		ArrayUtils.shuffle(objects);

		for (let i = 0; i < objects.length; i++) {
			this.createHiddenObject(scene, objects[i], i);
		}
	}

	pause(): void {
		for (const pointIndicator of this.pointIndicators) PointsIndicator.pauseEmitters(pointIndicator.emitters);
	}

	resume(): void {
		for (const pointIndicator of this.pointIndicators) PointsIndicator.resumeEmitters(pointIndicator.emitters);
	}

	enableNextHiddenObject(): HiddenObject {
		const obj = this.hiddenObjects[this.objectsEnabled];
		this.objectsEnabled++;
		obj.events.once(HiddenObject.ON_PRESSED, this.onObjectPressed, this);
		obj.enable();

		return obj;
	}

	protected createHiddenObject(scene: GameScene, objectData: HiddenObjectData, id: number): HiddenObject {
		const obj = new HiddenObject(objectData, scene, id);
		this.hiddenObjects.push(obj);
		//obj.events.once(HiddenObject.ON_PRESSED, onCollect, this);
		return obj;
	}

	protected onObjectPressed(hiddenObject: HiddenObject): void {
		this.objectsFound++;
		hiddenObject.image.setVisible(false);
		// this.score += hiddenObject.getPointTotal();

		const ogScore = hiddenObject.getPointTotal();
		let score = ogScore;
		const pts = new PointsIndicator(this.scene, hiddenObject.image.x, hiddenObject.image.y, score);

		let starAdjustmentX = 0;
		let starAdjustmentY = 0;
		const screenRatio = this.scene.viewport.width / this.scene.viewport.height;
		const goalRatio = 16 / 9;
		const ratioDif = screenRatio - goalRatio;
		if (screenRatio > goalRatio) starAdjustmentX = 540 * ratioDif;
		else starAdjustmentY = 540 * ratioDif;

		for (score; score > 0; score -= 5) {
			const star = this.scene.add.image(0, 0, "sparkle3");
			this.scene.add.existing(star);
			this.scene.alignTo.BottomCenter(star, pts);
			const scoreId = score;
			this.scene.hiddenObjGame.getLastBGLayer().add(star);
			this.scene.hiddenObjGame.getLastBGLayer().bringToTop(star);

			this.scene.tweens.add({
				targets: star,
				x: this.scene.scoreText.x - (125 + starAdjustmentX),
				duration: 500,
				ease: Phaser.Math.Easing.Quadratic.In,
				onComplete: () => {
					this.score += 5;
					this.scene.scoreText.setText(this.score.toString());
					star.destroy();

					if (scoreId == ogScore) {
						if (this.objectsFound == this.totalObjects) {
							this.events.emit(HiddenObjectLevel.ON_LEVEL_COMPLETED, this);
						}
					}
				},
				delay: score * 5,
			});

			this.scene.tweens.add({
				targets: star,
				y: (star.y + hiddenObject.image.y) / 2 + Phaser.Math.RND.between(-200, 0),
				duration: 250,
				delay: score * 5,
				onComplete: () => {
					this.scene.tweens.add({
						targets: star,
						y: this.scene.scoreText.y + starAdjustmentY,
						duration: 250,
					});
				},
			});
		}
		this.scene.hiddenObjGame.getLastBGLayer().add(pts);
		this.scene.hiddenObjGame.getLastBGLayer().bringToTop(pts);
		this.events.emit(HiddenObjectLevel.ON_OBJECT_COLLECTED, hiddenObject);
	}

	destroy(): void {
		if (this.hiddenObjects) {
			this.hiddenObjects.forEach((obj) => {
				if (obj) obj.destroy();
			});
		}
		this.hiddenObjects = [];
		this.pointIndicators = [];
	}
}
